import StyledDatePicker from '@/components/styled/StyledDatePicker';
import StyledInput from '@/components/styled/StyledInput';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { EstimateFormData } from '@/types/estimates/EstimateFormData';
import { format } from 'date-fns';
import { CheckIcon, Edit2Icon } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Control } from 'react-hook-form';
import { useTranslation } from '@/i18n';

interface EstimateSummaryParticipantsCardProps {
  control: Control<EstimateFormData>;
  adultParticipants: EstimateFormData['adults'];
  childParticipants: EstimateFormData['children'];
  isViewMode?: boolean;
}

const EstimateSummaryParticipantsCard = ({
  control,
  adultParticipants,
  childParticipants,
  isViewMode = false,
}: EstimateSummaryParticipantsCardProps) => {
  const { t } = useTranslation();
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base leading-6 font-semibold text-gray-700">
          {t('estimates.details.participants.title')}
        </CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col gap-2">
        {adultParticipants.map(({ firstName = '', lastName = '' }, index) => (
          <ParticipantRow
            key={`estimate-adult-${index}`}
            label={`${t('estimates.details.participants.adult')} ${index + 1}`}
            firstName={firstName}
            lastName={lastName}
            edit={
              !isViewMode
                ? {
                    control,
                    index,
                  }
                : undefined
            }
          />
        ))}
        {childParticipants.map(
          ({ firstName = '', lastName = '', birthDate }, index) => (
            <ParticipantRow
              key={`estimate-child-${index}`}
              label={`${t('estimates.details.participants.child')} ${index + 1}`}
              firstName={firstName}
              lastName={lastName}
              birthDate={birthDate}
              edit={
                !isViewMode
                  ? {
                      control,
                      index,
                    }
                  : undefined
              }
            />
          ),
        )}
      </CardContent>
    </Card>
  );
};

export default EstimateSummaryParticipantsCard;

interface ParticipantRowProps {
  label: string;
  firstName: string;
  lastName: string;
  birthDate?: Date;
  edit?: {
    control: Control<EstimateFormData>;
    index: number;
  };
}

const ParticipantRow = ({
  label,
  firstName,
  lastName,
  birthDate,
  edit,
}: ParticipantRowProps) => {
  const { t } = useTranslation();
  const participantText = `${label} - ${firstName} ${lastName} ${birthDate ? `- ${format(birthDate, 'dd/MM/yyyy')}` : ''}`;
  const [isEditEnabled, setIsEditEnabled] = useState(true);
  const fieldName = useMemo(
    () => (birthDate !== undefined ? 'children' : 'adults'),
    [birthDate],
  );

  return (
    <div className="flex gap-2 items-center">
      {isEditEnabled && edit !== undefined ? (
        <>
          <div className="w-96 max-w-1/3">
            <StyledInput
              control={edit.control}
              name={`${fieldName}.${edit.index}.firstName`}
              title={t('estimates.details.participants.firstName')}
              placeholder={t('estimates.details.participants.firstName')}
            />
          </div>
          <div className="w-96 max-w-1/3">
            <StyledInput
              control={edit.control}
              name={`${fieldName}.${edit.index}.lastName`}
              title={t('estimates.details.participants.lastName')}
              placeholder={t('estimates.details.participants.lastName')}
            />
          </div>
          {birthDate && (
            <div className="flex flex-col gap-1 w-96 max-w-1/3">
              <span className="text-sm leading-6 font-medium text-gray-700">
                {t('estimates.details.participants.birthDate')}
              </span>
              <StyledDatePicker
                control={edit.control}
                name={`${fieldName}.${edit.index}.birthDate`}
                placeholder={t('estimates.details.participants.birthDate')}
                acceptOnlyPastDates
              />
            </div>
          )}
          <CheckIcon
            className="size-6 stroke-brand-700 hover:cursor-pointer"
            onClick={() => {
              setIsEditEnabled(false);
            }}
          />
        </>
      ) : (
        <>
          <Checkbox checked={true} disabled />
          <span className="text-sm leading-6 font-medium text-gray-500">
            {participantText}
          </span>
          {edit !== undefined && !isEditEnabled && (
            <Edit2Icon
              className="size-4 stroke-brand-700 hover:cursor-pointer"
              onClick={() => {
                setIsEditEnabled(true);
              }}
            />
          )}
        </>
      )}
    </div>
  );
};
